import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CITIES_DATA } from '../data/landingData';
import Header from '../components/Header';
import HeroSection from '../components/sections/HeroSection';
import { TrustBar, AdvantagesSection, ProcessSection, ComparisonSection, SwissSpecificsSection, CitiesSection, TestimonialsSection, FaqSection } from '../components/sections/ContentSections';
import { FinalCtaSection, LandingFooter } from '../components/sections/LandingFooter';

const API = process.env.REACT_APP_BACKEND_URL;

function injectJsonLd(settings) {
  document.querySelectorAll('script[data-dynamic-jsonld]').forEach(el => el.remove());

  const org = {
    "@context": "https://schema.org", "@type": "Organization",
    "name": settings.organization_name || "Déclaration d'impôts",
    "alternateName": "declarationimpots.com", "url": "https://declarationimpots.com",
    "description": settings.organization_description || "",
    "areaServed": [{"@type": "Country", "name": "Suisse"}, {"@type": "Country", "name": "France"}, {"@type": "Country", "name": "Belgique"}],
    "slogan": settings.organization_slogan || "",
    "aggregateRating": {"@type": "AggregateRating", "ratingValue": settings.rating_value || "4.9", "reviewCount": settings.review_count || "98000", "bestRating": "5"}
  };
  const service = {
    "@context": "https://schema.org", "@type": "Service",
    "name": "Déclaration d'impôt par un expert fiscal",
    "provider": {"@type": "Organization", "name": "declarationimpots.com"},
    "description": settings.service_description || "",
    "serviceType": "Déclaration d'impôt",
    "award": "Plateforme n°1 de mise en relation fiscale en Suisse romande",
    "aggregateRating": {"@type": "AggregateRating", "ratingValue": settings.rating_value || "4.9", "reviewCount": settings.review_count || "98000", "bestRating": "5"}
  };
  const faq = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": (settings.faq_entries || []).map(e => ({"@type": "Question", "name": e.question, "acceptedAnswer": {"@type": "Answer", "text": e.answer}}))
  };

  [org, service, faq].forEach(data => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-dynamic-jsonld', 'true');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  });

  if (settings.custom_jsonld) {
    try {
      const custom = JSON.parse(settings.custom_jsonld);
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-dynamic-jsonld', 'true');
      script.textContent = JSON.stringify(custom);
      document.head.appendChild(script);
    } catch { /* invalid JSON, skip */ }
  }
}

export default function LandingPage() {
  const { city: cityParam } = useParams();
  const cityKey = cityParam?.toLowerCase().replace(/[éèê]/g, 'e').replace(/[âà]/g, 'a');
  const cityData = cityKey ? CITIES_DATA[cityKey] : null;
  const cityName = cityData?.name || '';
  const isCity = !!cityData;
  const [seoSettings, setSeoSettings] = useState(null);

  const fetchSeoSettings = useCallback(() => {
    axios.get(`${API}/api/public/site-settings`).then(({ data }) => {
      setSeoSettings(data);
      injectJsonLd(data);
    }).catch(() => {});
  }, []);

  useEffect(() => { fetchSeoSettings(); }, [fetchSeoSettings]);

  useEffect(() => {
    const metaTitle = isCity
      ? `Déclaration d'impôt ${cityName} 2025 - Expert fiscal ${cityData?.canton}`
      : (seoSettings?.meta_title || "Déclaration d'impôt 2025 en Suisse - Expert fiscal fiduciaire | Devis gratuit");
    const metaDescription = isCity
      ? `Faites remplir votre déclaration d'impôt à ${cityName} par un expert fiscal certifié. Économisez jusqu'à 20% sur vos impôts. Devis gratuit.`
      : (seoSettings?.meta_description || "Trouvez un expert fiscal fiduciaire en Suisse romande pour votre déclaration d'impôt 2025. Devis gratuit.");

    document.title = metaTitle;
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', metaDescription);
    const ogTitleEl = document.querySelector('meta[property="og:title"]');
    if (ogTitleEl) ogTitleEl.setAttribute('content', seoSettings?.og_title || metaTitle);
    const ogDescEl = document.querySelector('meta[property="og:description"]');
    if (ogDescEl) ogDescEl.setAttribute('content', seoSettings?.og_description || metaDescription);
  }, [isCity, cityName, cityData, seoSettings]);

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Header />
      <HeroSection isCity={isCity} cityName={cityName} cityData={cityData} />
      <TrustBar />
      <AdvantagesSection />
      <ProcessSection />
      <ComparisonSection />
      <SwissSpecificsSection />
      {!isCity && <CitiesSection />}
      <TestimonialsSection />
      <FaqSection />
      <FinalCtaSection />
      <LandingFooter seoSettings={seoSettings} />
    </div>
  );
}
